// src/components/modals/ProductSearchModal.tsx
import React, { useEffect, useState } from "react";

interface ProductSearchModalProps {
  isOpen: boolean;
  onClose: () => void;
}

const AI_API_URL = process.env.REACT_APP_AI_API_URL;

export const ProductSearchModal: React.FC<ProductSearchModalProps> = ({ isOpen, onClose }) => {
  const [selectedDays, setSelectedDays] = useState(7);
  const [selectedChannel, setSelectedChannel] = useState("all");
  const [data, setData] = useState<any>(null);
  const [loading, setLoading] = useState(false);
  const [availableChannels, setAvailableChannels] = useState<{ hls_url: string; name: string }[]>([]);

  // Dummy data structure matching backend: (total_searches, per_day_counts, last_three_queries)
  const DUMMY_PRODUCT_DATA = {
    all: {
      1: { 
        total: 67, 
        history: [["2024-01-20", 67]], 
        last_three: [
          ["2024-01-20T15:42:33", "wireless bluetooth headphones noise cancelling"],
          ["2024-01-20T14:28:15", "gaming laptop 16GB RAM RTX 4070"],
          ["2024-01-20T13:55:07", "organic coffee beans dark roast"]
        ]
      },
      7: { 
        total: 456, 
        history: [
          ["2024-01-14", 52],
          ["2024-01-15", 71],
          ["2024-01-16", 64],
          ["2024-01-17", 83],
          ["2024-01-18", 48],
          ["2024-01-19", 71],
          ["2024-01-20", 67]
        ],
        last_three: [
          ["2024-01-20T15:42:33", "wireless bluetooth headphones noise cancelling"],
          ["2024-01-20T14:28:15", "gaming laptop 16GB RAM RTX 4070"],
          ["2024-01-20T13:55:07", "organic coffee beans dark roast"]
        ]
      },
      14: { 
        total: 892, 
        history: [
          ["2024-01-07", 58], ["2024-01-08", 62], ["2024-01-09", 69], ["2024-01-10", 77], 
          ["2024-01-11", 45], ["2024-01-12", 61], ["2024-01-13", 74], ["2024-01-14", 52],
          ["2024-01-15", 71], ["2024-01-16", 64], ["2024-01-17", 83], ["2024-01-18", 48],
          ["2024-01-19", 71], ["2024-01-20", 67]
        ],
        last_three: [
          ["2024-01-20T15:42:33", "wireless bluetooth headphones noise cancelling"],
          ["2024-01-20T14:28:15", "gaming laptop 16GB RAM RTX 4070"],
          ["2024-01-20T13:55:07", "organic coffee beans dark roast"]
        ]
      },
      30: { 
        total: 1834, 
        history: [
          ["2024-01-01", 63], ["2024-01-02", 68], ["2024-01-03", 55], ["2024-01-04", 79],
          ["2024-01-05", 47], ["2024-01-06", 72], ["2024-01-07", 58], ["2024-01-08", 62],
          ["2024-01-09", 69], ["2024-01-10", 77], ["2024-01-11", 45], ["2024-01-12", 61],
          ["2024-01-13", 74], ["2024-01-14", 52], ["2024-01-15", 71], ["2024-01-16", 64],
          ["2024-01-17", 83], ["2024-01-18", 48], ["2024-01-19", 71], ["2024-01-20", 67],
          ["2024-01-21", 65], ["2024-01-22", 59], ["2024-01-23", 76], ["2024-01-24", 81],
          ["2024-01-25", 49], ["2024-01-26", 73], ["2024-01-27", 60], ["2024-01-28", 66],
          ["2024-01-29", 57], ["2024-01-30", 75]
        ],
        last_three: [
          ["2024-01-30T16:23:41", "4K monitor 32 inch curved gaming"],
          ["2024-01-30T15:17:22", "electric standing desk adjustable height"],
          ["2024-01-30T14:09:18", "mechanical keyboard RGB backlit"]
        ]
      }
    },
    bloombergtvplus: {
      1: { 
        total: 23, 
        history: [["2024-01-20", 23]], 
        last_three: [
          ["2024-01-20T15:42:33", "Tesla stock analysis report"],
          ["2024-01-20T14:28:15", "Federal Reserve interest rates"],
          ["2024-01-20T13:55:07", "S&P 500 quarterly earnings"]
        ]
      },
      7: { 
        total: 167, 
        history: [
          ["2024-01-14", 19],
          ["2024-01-15", 26],
          ["2024-01-16", 22],
          ["2024-01-17", 31],
          ["2024-01-18", 17],
          ["2024-01-19", 29],
          ["2024-01-20", 23]
        ],
        last_three: [
          ["2024-01-20T15:42:33", "Tesla stock analysis report"],
          ["2024-01-20T14:28:15", "Federal Reserve interest rates"],
          ["2024-01-20T13:55:07", "S&P 500 quarterly earnings"]
        ]
      },
      14: { 
        total: 334, 
        history: [
          ["2024-01-07", 21], ["2024-01-08", 24], ["2024-01-09", 27], ["2024-01-10", 33], 
          ["2024-01-11", 16], ["2024-01-12", 25], ["2024-01-13", 30], ["2024-01-14", 19],
          ["2024-01-15", 26], ["2024-01-16", 22], ["2024-01-17", 31], ["2024-01-18", 17],
          ["2024-01-19", 29], ["2024-01-20", 23]
        ],
        last_three: [
          ["2024-01-20T15:42:33", "Tesla stock analysis report"],
          ["2024-01-20T14:28:15", "Federal Reserve interest rates"],
          ["2024-01-20T13:55:07", "S&P 500 quarterly earnings"]
        ]
      },
      30: { 
        total: 712, 
        history: [
          ["2024-01-01", 24], ["2024-01-02", 27], ["2024-01-03", 20], ["2024-01-04", 32],
          ["2024-01-05", 18], ["2024-01-06", 28], ["2024-01-07", 21], ["2024-01-08", 24],
          ["2024-01-09", 27], ["2024-01-10", 33], ["2024-01-11", 16], ["2024-01-12", 25],
          ["2024-01-13", 30], ["2024-01-14", 19], ["2024-01-15", 26], ["2024-01-16", 22],
          ["2024-01-17", 31], ["2024-01-18", 17], ["2024-01-19", 29], ["2024-01-20", 23],
          ["2024-01-21", 25], ["2024-01-22", 22], ["2024-01-23", 29], ["2024-01-24", 34],
          ["2024-01-25", 19], ["2024-01-26", 28], ["2024-01-27", 23], ["2024-01-28", 26],
          ["2024-01-29", 21], ["2024-01-30", 30]
        ],
        last_three: [
          ["2024-01-30T16:23:41", "cryptocurrency market trends Bitcoin"],
          ["2024-01-30T15:17:22", "oil prices WTI crude analysis"],
          ["2024-01-30T14:09:18", "inflation data consumer price index"]
        ]
      }
    }
  };

  const [hoveredBar, setHoveredBar] = useState<{index: number, count: number, date: string} | null>(null);

//   useEffect(() => {
//     if (isOpen) {
//       setLoading(true);
//       setTimeout(() => {
//         const channelData = DUMMY_PRODUCT_DATA[selectedChannel as keyof typeof DUMMY_PRODUCT_DATA];
//         setData(channelData[selectedDays as keyof typeof channelData]);
//         setLoading(false);
//       }, 800);
//     }
//   }, [isOpen, selectedDays, selectedChannel]);


    useEffect(() => {
    const controller = new AbortController();
    const loadChannels = async () => {
        try {
        const res = await fetch(`${AI_API_URL}/active-channels`, { signal: controller.signal });
        if (!res.ok) throw new Error("Failed to load channels");
        const json = await res.json();
        setAvailableChannels(json.channels || []);
        } catch (e) {
        if ((e as any).name !== "AbortError") console.error("Error loading channels", e);
        }
    };
    loadChannels();
    return () => controller.abort();
    }, []);



    useEffect(() => {
        if (!isOpen) return () => {};

        const controller = new AbortController();
        setLoading(true);

        const channelParam = selectedChannel === "all" ? "all" : encodeURIComponent(selectedChannel);

        fetch(
            `${AI_API_URL}/fetch-product-lens-stats?days=${selectedDays}&channel=${channelParam}`,
            { signal: controller.signal }
        )
            .then((r) => {
            if (!r.ok) throw new Error("Product Lens fetch failed");
            return r.json();
            })
            .then((json) => {
            setData(json);
            })
            .catch((err) => {
            if (err.name !== "AbortError") console.error(err);
            })
            .finally(() => setLoading(false));

        return () => controller.abort();
        }, [isOpen, selectedDays, selectedChannel]);

  const maxValue = data ? Math.max(...data.history.map((item: any) => item[1])) : 0;

  if (!isOpen) return null;

  return (
    <div
      role="presentation"
      style={{
        position: "fixed",
        inset: 0,
        background: "rgba(0, 0, 0, 0.8)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        zIndex: 1000,
        backdropFilter: "blur(4px)",
        animation: isOpen ? "fadeIn 0.3s ease-out" : "fadeOut 0.3s ease-out",
      }}
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
      onKeyDown={(e) => {
        if (e.key === 'Escape') {
          e.preventDefault();
          onClose();
        }
      }}
      tabIndex={-1}
    >
      <div
        role="dialog"
        aria-modal="true"
        style={{
          background: "rgba(255, 255, 255, 0.1)",
          backdropFilter: "blur(20px)",
          borderRadius: "20px",
          padding: "32px",
          width: "90%",
          maxWidth: "900px",
          maxHeight: "90vh",
          overflowY: "auto",
          border: "1px solid rgba(255, 255, 255, 0.2)",
          boxShadow: "0 25px 50px -12px rgba(0, 0, 0, 0.5)",
          animation: isOpen ? "zoomIn 0.4s cubic-bezier(0.4, 0, 0.2, 1)" : "",
        }}
      >
        {/* Header */}
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "32px" }}>
          <div>
            <h2 style={{ color: "#ffffff", fontSize: "24px", fontWeight: "700", margin: 0 }}>
              Product Lens Analytics
            </h2>
            <p style={{ color: "rgba(255, 255, 255, 0.7)", fontSize: "14px", margin: "4px 0 0 0" }}>
              Visual search queries and usage patterns
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            style={{
              background: "rgba(255, 255, 255, 0.1)",
              border: "1px solid rgba(255, 255, 255, 0.2)",
              borderRadius: "8px",
              color: "#ffffff",
              padding: "8px 12px",
              cursor: "pointer",
              fontSize: "14px",
            }}
          >
            ✕
          </button>
        </div>

        {/* Controls */}
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "16px", marginBottom: "24px" }}>
          {/* Time Period Selector */}
          <div>
            <p style={{ margin: 0, marginBottom: 8, color: "rgba(255, 255, 255, 0.8)", fontSize: 14, fontWeight: 500 }}>
              Time Period
            </p>
            <select
              id="product-period"
              aria-label="Time period"
              value={selectedDays}
              onChange={(e) => setSelectedDays(Number(e.target.value))}
              style={{
                display: "block",
                width: "100%",
                background: "rgba(255,255,255,0.1)",
                border: "1px solid rgba(255,255,255,0.2)",
                borderRadius: 8,
                color: "#ffffff",
                padding: "8px 12px",
                fontSize: 14,
                outline: "none",
              }}
            >
              <option value={1} style={{ background: "#1f2937", color: "#ffffff" }}>Last 24 hours</option>
              <option value={7} style={{ background: "#1f2937", color: "#ffffff" }}>Last 7 days</option>
              <option value={14} style={{ background: "#1f2937", color: "#ffffff" }}>Last 14 days</option>
              <option value={30} style={{ background: "#1f2937", color: "#ffffff" }}>Last 30 days</option>
            </select>
          </div>

          {/* Channel Selector */}
          <div>
            <p style={{ margin: 0, marginBottom: 8, color: "rgba(255, 255, 255, 0.8)", fontSize: 14, fontWeight: 500 }}>
              Channel
            </p>
            <select
            id="product-channel"
            aria-label="Channel"
            value={selectedChannel}
            onChange={(e) => setSelectedChannel(e.target.value)}
            style={{
                display: "block",
                width: "100%",
                background: "rgba(255,255,255,0.1)",
                border: "1px solid rgba(255,255,255,0.2)",
                borderRadius: 8,
                color: "#ffffff",
                padding: "8px 12px",
                fontSize: 14,
                outline: "none",
            }}
            >
            <option value="all" style={{ background: "#1f2937", color: "#ffffff" }}>
                All Channels
            </option>
            {availableChannels.map((ch) => (
                <option key={ch.name} value={ch.name} style={{ background: "#1f2937", color: "#ffffff" }}>
                {ch.name}
                </option>
            ))}
            </select>
          </div>
        </div>

        {/* Chart */}
        <div style={{ marginBottom: "32px" }}>
          <h3 style={{ color: "#ffffff", fontSize: "18px", fontWeight: "600", marginBottom: "16px" }}>
            Daily Search Volume {selectedChannel !== "all" && `- ${selectedChannel}`}
          </h3>
          <div
            style={{
              background: "rgba(255, 255, 255, 0.05)",
              borderRadius: "12px",
              padding: "24px",
              border: "1px solid rgba(255, 255, 255, 0.1)",
              height: "300px",
              display: "flex",
              position: "relative",
            }}
          >
            {loading ? (
              <div style={{ display: "flex", alignItems: "center", justifyContent: "center", width: "100%", color: "rgba(255, 255, 255, 0.7)" }}>
                Loading chart...
              </div>
            ) : (
              <>
                {/* Y-Axis */}
                <div
                  style={{
                    width: "50px",
                    height: "240px",
                    display: "flex",
                    flexDirection: "column",
                    justifyContent: "space-between",
                    alignItems: "flex-end",
                    paddingRight: "12px",
                    marginRight: "16px",
                    borderRight: "1px solid rgba(255, 255, 255, 0.1)",
                  }}
                >
                  {[...Array(6)].map((_, i) => {
                    const value = Math.round((maxValue * (5 - i)) / 5);
                    return (
                      <div
                        key={i}
                        style={{
                          color: "rgba(255, 255, 255, 0.6)",
                          fontSize: "11px",
                          fontWeight: "500",
                          textAlign: "right",
                          lineHeight: "1",
                        }}
                      >
                        {value > 0 ? value : 0}
                      </div>
                    );
                  })}
                </div>

                {/* Chart Area */}
                <div
                  style={{
                    flex: 1,
                    height: "240px",
                    display: "flex",
                    alignItems: "end",
                    justifyContent: "space-around",
                    gap: "4px",
                    position: "relative",
                  }}
                >
                  {/* Horizontal grid lines */}
                  {[...Array(6)].map((_, i) => (
                    <div
                      key={i}
                      style={{
                        position: "absolute",
                        left: 0,
                        right: 0,
                        top: `${(i * 240) / 5}px`,
                        height: "1px",
                        background: "rgba(255, 255, 255, 0.05)",
                        pointerEvents: "none",
                      }}
                    />
                  ))}

                    {/* Bars */}
                    {data?.history.map((item: any, index: number) => {
                    const [date, count] = item;
                    const height = (count / maxValue) * 240;

                    return (
                        <div
                        key={date}
                        style={{
                            position: "relative",
                            flex: 1,
                            height: "100%",
                            display: "flex",
                            alignItems: "flex-end",
                            justifyContent: "center",
                        }}
                        >
                        <div
                            style={{
                            height: `${height}px`,
                            width: "100%",
                            maxWidth: 40,
                            background: "linear-gradient(to top, #06b6d4, #67e8f9)",
                            borderRadius: "4px 4px 0 0",
                            boxShadow: "0 4px 12px rgba(6,182,212,0.3)",
                            animation: `barGrowUp 1s ease-out ${index * 0.1}s both`,
                            cursor: "pointer",
                            }}
                            onMouseEnter={() => setHoveredBar({ index, count, date })}
                            onMouseLeave={() => setHoveredBar(null)}
                        />

                        {hoveredBar?.index === index && (
                            <div
                            style={{
                                position: "absolute",
                                bottom: `${height + 10}px`,
                                left: "50%",
                                transform: "translateX(-50%)",
                                background: "rgba(0,0,0,.8)",
                                color: "#67e8f9",
                                padding: "4px 8px",
                                borderRadius: 4,
                                fontSize: 12,
                                fontWeight: 600,
                                pointerEvents: "none",
                            }}
                            >
                            {count}
                            </div>
                        )}

                        <div
                            style={{
                            position: "absolute",
                            bottom: -18,
                            left: "50%",
                            transform: "translateX(-50%)",
                            color: "rgba(255,255,255,.6)",
                            fontSize: 10,
                            lineHeight: 1.2,
                            textAlign: "center",
                            whiteSpace: "nowrap",
                            }}
                        >
                            {new Date(date).toLocaleDateString([], { month: "short", day: "numeric" })}
                        </div>
                        </div>
                    );
                    })}
                </div>
              </>
            )}
          </div>
        </div>

        {/* Recent Searches Section */}
        <div style={{ marginBottom: "32px" }}>
          <h3 style={{ color: "#ffffff", fontSize: "18px", fontWeight: "600", marginBottom: "16px" }}>
            Recent Search Queries
          </h3>
          <div style={{ display: "flex", flexDirection: "column", gap: "12px" }}>
            {loading ? (
              <div style={{ color: "rgba(255, 255, 255, 0.7)", textAlign: "center", padding: "20px" }}>
                Loading recent searches...
              </div>
            ) : (
              data?.last_three.map((item: any, index: number) => {
                const [timestamp, query] = item;
                const date = new Date(timestamp);
                return (
                  <div
                    key={index}
                    style={{
                      background: "rgba(255, 255, 255, 0.08)",
                      borderRadius: "8px",
                      padding: "16px",
                      border: "1px solid rgba(255, 255, 255, 0.1)",
                      display: "flex",
                      alignItems: "center",
                      gap: "16px",
                    }}
                  >
                    <div
                      style={{
                        width: "8px",
                        height: "8px",
                        borderRadius: "50%",
                        background: "#67e8f9",
                        flexShrink: 0,
                      }}
                    />
                    <div style={{ flex: 1 }}>
                        <div
                        style={{
                            color: "#ffffff",
                            fontSize: "14px",
                            fontWeight: "500",
                            marginBottom: "4px",
                            wordBreak: "break-word",
                        }}
                        >
                        <a
                            href={query}
                            target="_blank"
                            rel="noopener noreferrer"
                            style={{
                            color: "#ffffff",
                            textDecoration: "underline",
                            cursor: "pointer",
                            wordBreak: "break-word",
                            }}
                        >
                            {query}
                        </a>
                        </div>
                      <div
                        style={{
                          color: "rgba(255, 255, 255, 0.6)",
                          fontSize: "12px",
                        }}
                      >
                        {date.toLocaleDateString([], { 
                          weekday: 'short', 
                          month: 'short', 
                          day: 'numeric',
                          hour: '2-digit',
                          minute: '2-digit'
                        })}
                      </div>
                    </div>
                  </div>
                );
              })
            )}
          </div>
        </div>

        {/* Summary Stats */}
        <div style={{ display: "grid", gridTemplateColumns: "1fr", gap: "20px" }}>
          <div
            style={{
              background: "rgba(255, 255, 255, 0.08)",
              borderRadius: "12px",
              padding: "20px",
              border: "1px solid rgba(255, 255, 255, 0.1)",
              textAlign: "center",
            }}
          >
            <div style={{ color: "#67e8f9", fontSize: "28px", fontWeight: "700", marginBottom: "4px" }}>
              {data?.total.toLocaleString() || 0}
            </div>
            <div style={{ color: "rgba(255, 255, 255, 0.7)", fontSize: "14px" }}>Total Searches</div>
          </div>
        </div>
      </div>
    </div>
  );
};